import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AuthService from "../../service";
import {
  enviarFeedback,
  enviarMensagem,
  lerContextoFiltro,
} from "../../service/chatService";
import { extrairSugestoes } from "./sugestoes";

const SUGESTOES = [
  "Como foi meu mês?",
  "Qual meu melhor dia e horário?",
  "O que mais vende?",
  "Quanto vendo na mesa vs delivery?",
  "Tô vendendo algo no prejuízo?",
  "Quais produtos combinam pra fazer combo?",
  "Como meus clientes pagam? (pix, cartão, dinheiro)",
  "Quem são meus melhores clientes?",
  "Meu CMV está saudável?",
  "O que vendo em dia de chuva?",
];

// A espera real chega a 15-20s quando as queries rodam a frio; frase parada
// parece travamento. Rotação puramente cosmética, para na última.
const FRASES_ESPERA = [
  "Analisando os dados...",
  "Consultando as vendas...",
  "Cruzando os números...",
  "Conferindo os indicadores...",
  "Quase lá...",
];

// Mensagens exibidas ao usuário (separadas do histórico opaco do backend).
const MSG_BOAS_VINDAS = {
  autor: "ia",
  texto:
    "Oi! Eu sou a **Sisa**, sua consultora de dados. Posso analisar vendas, produtos, clientes, operação e CMV das unidades selecionadas no filtro acima. O que você quer saber?",
};

// ---- Persistência da conversa (localStorage, por usuário) -------------------
// A conversa sumia a cada F5. Guarda mensagens + histórico opaco por 24h; tudo
// em try/catch: storage indisponível ou payload torto = conversa nova, nunca erro.
const CHAVE_CONVERSA = "chat_sisa_conversa";
const CONVERSA_TTL_MS = 24 * 60 * 60 * 1000;
const CONVERSA_TAMANHO_MAX = 200000;

function obterDono() {
  try {
    const p = AuthService.profile();
    return String(
      (p && (p.id || p.idUsuario || p.username || p.email)) || "anon"
    );
  } catch (e) {
    return "anon";
  }
}

function carregarConversa() {
  try {
    const raw = localStorage.getItem(CHAVE_CONVERSA);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || c.v !== 1 || c.dono !== obterDono()) return null;
    if (!c.salvoEm || Date.now() - c.salvoEm > CONVERSA_TTL_MS) return null;
    if (!Array.isArray(c.mensagens) || !Array.isArray(c.historico)) return null;
    const validas = c.mensagens.every(
      (m) => m && typeof m.texto === "string" && (m.autor === "ia" || m.autor === "user")
    );
    if (!validas || c.mensagens.length < 2) return null;
    return { mensagens: c.mensagens, historico: c.historico };
  } catch (e) {
    return null;
  }
}

function salvarConversa(mensagens, historico) {
  try {
    if (!mensagens || mensagens.length < 2) {
      localStorage.removeItem(CHAVE_CONVERSA);
      return;
    }
    const payload = JSON.stringify({
      v: 1,
      dono: obterDono(),
      salvoEm: Date.now(),
      mensagens,
      historico,
    });
    if (payload.length > CONVERSA_TAMANHO_MAX) return; // conversa gigante: não persiste
    localStorage.setItem(CHAVE_CONVERSA, payload);
  } catch (e) {
    // storage cheio/bloqueado: segue sem persistir
  }
}

export default function ChatWidget() {
  const [conversaInicial] = useState(() => carregarConversa());
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState(
    conversaInicial ? conversaInicial.mensagens : [MSG_BOAS_VINDAS]
  );
  const [historico, setHistorico] = useState(
    conversaInicial ? conversaInicial.historico : []
  );
  const [texto, setTexto] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [fraseEspera, setFraseEspera] = useState(0);
  const fimRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (aberto) {
      fimRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [mensagens, aberto, carregando]);

  // Mantém o foco no campo de digitação: ao abrir o painel e sempre que a IA
  // termina de responder (carregando volta a false), devolve o cursor pro input
  // — antes ele perdia o foco a cada pergunta.
  useEffect(() => {
    if (aberto && !carregando) {
      inputRef.current?.focus();
    }
  }, [aberto, carregando]);

  // Rotação das frases de espera enquanto a resposta não chega.
  useEffect(() => {
    if (!carregando) return undefined;
    setFraseEspera(0);
    const id = setInterval(
      () => setFraseEspera((f) => Math.min(f + 1, FRASES_ESPERA.length - 1)),
      3500
    );
    return () => clearInterval(id);
  }, [carregando]);

  // Persiste a conversa a cada mudança (e limpa quando volta ao estado inicial).
  useEffect(() => {
    salvarConversa(mensagens, historico);
  }, [mensagens, historico]);

  const enviar = async (pergunta) => {
    const conteudo = (pergunta ?? texto).trim();
    if (!conteudo || carregando) return;

    setTexto("");
    setMensagens((prev) => [...prev, { autor: "user", texto: conteudo }]);
    setCarregando(true);

    try {
      const { unidades, periodo } = lerContextoFiltro();
      const resp = await enviarMensagem({
        mensagem: conteudo,
        historico,
        unidades,
        periodo,
      });

      const { texto: textoLimpo, sugestoes } = extrairSugestoes(
        resp.resposta || ""
      );

      setHistorico(resp.historico || []);
      setMensagens((prev) => [
        ...prev,
        {
          autor: "ia",
          texto: textoLimpo || "(sem resposta)",
          respostaId: resp.respostaId,
          sugestoes,
        },
      ]);
    } catch (e) {
      console.error("Erro no chat:", e);
      setMensagens((prev) => [
        ...prev,
        {
          autor: "ia",
          texto:
            "Tive um problema pra responder agora. Verifique se você já clicou em **Buscar** no filtro e tente de novo.",
        },
      ]);
    } finally {
      setCarregando(false);
    }
  };

  // 👍/👎: marca na hora e manda pro backend sem bloquear nada.
  const avaliar = (indice, util) => {
    setMensagens((prev) =>
      prev.map((m, i) => (i === indice ? { ...m, feedback: util ? "up" : "down" } : m))
    );
    const m = mensagens[indice];
    if (m && m.respostaId) {
      enviarFeedback({ respostaId: m.respostaId, util }).catch(() => {});
    }
  };

  const novaConversa = () => {
    setMensagens([MSG_BOAS_VINDAS]);
    setHistorico([]);
    setTexto("");
    try {
      localStorage.removeItem(CHAVE_CONVERSA);
    } catch (e) {
      // sem storage, sem problema
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const ultima = mensagens[mensagens.length - 1];
  const sugestoesFollowUp =
    !carregando && ultima && ultima.autor === "ia" && ultima.sugestoes
      ? ultima.sugestoes
      : [];

  return (
    <>
      {/* Botão flutuante */}
      <Zoom in={!aberto}>
        <Fab
          color="primary"
          aria-label="Abrir a Sisa, consultora de dados"
          onClick={() => setAberto(true)}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}
        >
          <SmartToyIcon />
        </Fab>
      </Zoom>

      {/* Painel de chat */}
      <Zoom in={aberto}>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: { xs: "calc(100vw - 32px)", sm: 400 },
            height: { xs: "calc(100vh - 120px)", sm: 560 },
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.5,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <SmartToyIcon fontSize="small" />
            <Box sx={{ flexGrow: 1, lineHeight: 1.1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Sisa
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Consultora de dados
              </Typography>
            </Box>
            {mensagens.length > 1 && (
              <Tooltip title="Nova conversa">
                <IconButton
                  size="small"
                  onClick={novaConversa}
                  sx={{ color: "inherit" }}
                  aria-label="Começar uma nova conversa"
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={() => setAberto(false)}
              sx={{ color: "inherit" }}
              aria-label="Fechar"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Mensagens */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 1.5,
              bgcolor: "background.default",
            }}
          >
            {mensagens.map((m, i) => (
              <Bolha
                key={i}
                autor={m.autor}
                texto={m.texto}
                feedback={m.feedback}
                podeAvaliar={m.autor === "ia" && Boolean(m.respostaId)}
                onAvaliar={(util) => avaliar(i, util)}
              />
            ))}

            {carregando && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {FRASES_ESPERA[fraseEspera]}
                </Typography>
              </Box>
            )}

            {/* Sugestões só no início da conversa */}
            {mensagens.length === 1 && !carregando && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1, px: 1 }}>
                {SUGESTOES.map((s) => (
                  <Chip key={s} rotulo={s} onClick={() => enviar(s)} />
                ))}
              </Box>
            )}

            {/* Perguntas de continuação sugeridas pela própria Sisa */}
            {sugestoesFollowUp.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5, px: 1 }}>
                {sugestoesFollowUp.map((s) => (
                  <Chip key={s} rotulo={s} onClick={() => enviar(s)} />
                ))}
              </Box>
            )}

            <div ref={fimRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              p: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              autoFocus
              inputRef={inputRef}
              placeholder="Pergunte sobre seus dados..."
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <IconButton
              color="primary"
              onClick={() => enviar()}
              disabled={carregando || !texto.trim()}
              aria-label="Enviar"
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
}

function Chip({ rotulo, onClick }) {
  // Botão de verdade: foco e Enter/Espaço pra teclado e leitor de tela.
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        font: "inherit",
        bgcolor: "transparent",
        color: "inherit",
        textAlign: "left",
        border: 1,
        borderColor: "divider",
        borderRadius: 4,
        px: 1.5,
        py: 0.5,
        fontSize: 13,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {rotulo}
    </Box>
  );
}

function Bolha({ autor, texto, feedback, podeAvaliar, onAvaliar }) {
  const ehIA = autor === "ia";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: ehIA ? "row" : "row-reverse",
        gap: 1,
        mb: 1.5,
        alignItems: "flex-start",
      }}
    >
      {ehIA && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main" }}>
          <SmartToyIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: "85%" }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: ehIA ? "background.paper" : "primary.main",
            color: ehIA ? "text.primary" : "primary.contrastText",
            border: ehIA ? 1 : 0,
            borderColor: "divider",
            fontSize: 14,
            lineHeight: 1.5,
            textAlign: "left",
            wordBreak: "break-word",
            "& > :first-of-type": { mt: 0 },
            "& > :last-child": { mb: 0 },
            "& p": { m: 0, mb: 1 },
            "& strong": { fontWeight: 700 },
            "& h1, & h2, & h3, & h4": {
              fontSize: 14,
              fontWeight: 700,
              m: 0,
              mt: 1.5,
              mb: 0.5,
              color: ehIA ? "primary.main" : "inherit",
            },
            "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
            "& li": { mb: 0.25 },
            "& table": { borderCollapse: "collapse", width: "100%", my: 1, fontSize: 13 },
            "& th, & td": { border: 1, borderColor: "divider", px: 0.75, py: 0.5, textAlign: "left" },
            "& th": { bgcolor: "action.hover", fontWeight: 700 },
            "& code": { bgcolor: "action.hover", px: 0.5, borderRadius: 0.5, fontSize: 13 },
          }}
        >
          {ehIA ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{texto}</ReactMarkdown>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {texto}
            </Typography>
          )}
        </Box>
        {podeAvaliar && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, ml: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onAvaliar(true)}
              disabled={Boolean(feedback)}
              aria-label="Resposta útil"
              sx={{ p: 0.25, color: feedback === "up" ? "primary.main" : "text.disabled" }}
            >
              {feedback === "up" ? (
                <ThumbUpAltIcon sx={{ fontSize: 15 }} />
              ) : (
                <ThumbUpOffAltIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onAvaliar(false)}
              disabled={Boolean(feedback)}
              aria-label="Resposta não ajudou"
              sx={{ p: 0.25, color: feedback === "down" ? "primary.main" : "text.disabled" }}
            >
              {feedback === "down" ? (
                <ThumbDownAltIcon sx={{ fontSize: 15 }} />
              ) : (
                <ThumbDownOffAltIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
